import { api } from "./api";

export const STORAGE_KEY = "pfn_journey_v1";
const SID_KEY = "pfn_sid";

export const PURPOSES = ["To live in (self-use)", "Investment / appreciation", "For my parents / family", "Retirement home", "Rental income"];
export const PROPERTY_TYPES = ["Apartment", "Villa / Row house", "Plot", "Commercial"];
export const CONFIGS = {
  Apartment: ["1 BHK", "2 BHK", "3 BHK", "4 BHK or larger"],
  "Villa / Row house": ["3 bedrooms · up to 2,000 sq ft", "4 bedrooms · 2,000–3,500 sq ft", "5+ bedrooms · 3,500+ sq ft"],
  Plot: ["Up to 1,500 sq ft", "1,500–3,000 sq ft", "3,000–5,000 sq ft", "5,000+ sq ft / farm plot"],
  Commercial: ["Office · up to 1,000 sq ft", "Office · 1,000+ sq ft", "Retail shop · up to 500 sq ft", "Retail shop · 500+ sq ft"],
};
export const BUDGETS = {
  Apartment: ["Under ₹50 Lakh", "₹50 Lakh – ₹1 Cr", "₹1 – 2 Cr", "₹2 – 5 Cr", "₹5 Cr+"],
  "Villa / Row house": ["Under ₹1.5 Cr", "₹1.5 – 3 Cr", "₹3 – 5 Cr", "₹5 Cr+"],
  Plot: ["Under ₹30 Lakh", "₹30 – 75 Lakh", "₹75 Lakh – ₹1.5 Cr", "₹1.5 Cr+"],
  Commercial: ["Under ₹75 Lakh", "₹75 Lakh – ₹2 Cr", "₹2 – 5 Cr", "₹5 Cr+"],
};
export const POSSESSIONS = ["Ready to move in", "Within 1 year", "1 – 3 years", "3+ years (under construction is fine)", "Flexible"];
export const PRIORITIES = [
  "Trusted, reputed developer", "Good connectivity", "Schools & hospitals nearby", "Rental potential",
  "Appreciation potential", "Gated security for family", "Lifestyle amenities", "Vastu compliant", "Low maintenance",
];
export const VISITS = ["Within the next month", "In 1 – 3 months", "In 3 – 6 months", "6+ months away", "No visit planned — prefer remote"];
export const NOT_DECIDED = "Not decided yet";
export const ANY_AREA = "Open to suggestions";

export const STEPS = [
  "opening", "intro", "compare", "purpose", "city", "area", "type", "config",
  "budget", "possession", "priorities", "visit", "searching", "results", "contact", "thanks",
];
export const QUESTION_STEPS = ["purpose", "city", "area", "type", "config", "budget", "possession", "priorities", "visit"];

export const STEP_EVENT = {
  opening: "landing_view", intro: "intro_view", compare: "comparison_view", purpose: "questionnaire_start",
  results: "results_view", contact: "contact_view",
};
export const ANSWER_EVENT = {
  purpose: "q_purpose", city: "q_city", area: "q_area", type: "q_type", config: "q_config",
  budget: "q_budget", possession: "q_possession", priorities: "q_priorities", visit: "q_visit",
};

export function nextStep(step, answers) {
  let i = STEPS.indexOf(step) + 1;
  if (STEPS[i] === "area" && (!answers.city || answers.city === NOT_DECIDED)) i += 1;
  return STEPS[i];
}

export function prevStep(step, answers) {
  let i = STEPS.indexOf(step) - 1;
  if (STEPS[i] === "area" && (!answers.city || answers.city === NOT_DECIDED)) i -= 1;
  return STEPS[Math.max(0, i)];
}

export function questionProgress(step, answers) {
  const list = QUESTION_STEPS.filter((s) => s !== "area" || (answers.city && answers.city !== NOT_DECIDED));
  return { index: list.indexOf(step) + 1, total: list.length };
}

const STATE_TTL_MS = 30 * 24 * 3600 * 1000;

export function loadState() {
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    return s.savedAt && Date.now() - s.savedAt > STATE_TTL_MS ? {} : s;
  } catch {
    return {};
  }
}

export function saveState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
}

export function sessionId() {
  let sid = localStorage.getItem(SID_KEY);
  if (!sid) {
    sid = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now()).replace(/-/g, "");
    localStorage.setItem(SID_KEY, sid);
  }
  return sid;
}

const sent = new Set();
export function track(event) {
  if (!event || sent.has(event)) return;
  sent.add(event);
  api.post("/public/events", { session_id: sessionId(), event }).catch(() => {});
}

const TZ_COUNTRY = {
  "Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Qatar": "QA", "Asia/Muscat": "OM", "Asia/Kuwait": "KW",
  "Asia/Bahrain": "BH", "Europe/London": "GB", "Asia/Singapore": "SG", "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA", "America/Winnipeg": "CA",
  "America/Halifax": "CA", "Pacific/Auckland": "NZ", "Europe/Berlin": "DE", "Europe/Paris": "FR",
  "Europe/Amsterdam": "NL", "Europe/Dublin": "IE", "Asia/Hong_Kong": "HK", "Asia/Tokyo": "JP", "Asia/Kuala_Lumpur": "MY",
};

export function detectCountry() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (TZ_COUNTRY[tz]) return TZ_COUNTRY[tz];
  if (tz.startsWith("Australia/")) return "AU";
  if (tz.startsWith("America/")) return "US";
  const region = (navigator.language || "").split("-")[1];
  return region && region.length === 2 ? region.toUpperCase() : "AE";
}

export function flagOf(iso) {
  return iso.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

const regionNames = typeof Intl.DisplayNames === "function" ? new Intl.DisplayNames(["en"], { type: "region" }) : null;
export function countryName(iso) {
  try {
    return regionNames?.of(iso) || iso;
  } catch {
    return iso;
  }
}

export function readUtm() {
  const p = new URLSearchParams(window.location.search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"].forEach((k) => {
    if (p.get(k)) utm[k] = p.get(k);
  });
  return utm;
}
